import { Link } from "react-router-dom";
import { Mail, Clock, ArrowRight } from "lucide-react";
import PageShell from "@/components/site/PageShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  {
    q: "How do I place an order?",
    a: "Browse creators on the Discover tab, open a business, add meals to your cart, and choose pickup or delivery at checkout. You can order ASAP or schedule a custom pickup or delivery time in 30-minute slots — your creator confirms your time.",
  },
  {
    q: "How are delivery fees calculated?",
    a: "Creators set flat or per-mile delivery fees. The exact delivery fee is shown before you pay.",
  },
  {
    q: "How do I become a Dishly creator?",
    a: "Dishly is for all independent food creators — home cooks, food trucks, pop-ups, market vendors, bakers, meal preppers, and caterers. We're currently accepting Founding Creator applications from your area — tap \"Apply to become a Dishly creator\" on our homepage to apply and request a demo. Once the app launches on the App Store, you'll also be able to sign up in-app under \"Sell on Dishly,\" complete your business profile, upload your food-safety documents, and connect your payouts through Stripe.",
  },
  {
    q: "Payments and tips?",
    a: "Payments are processed securely via Stripe. Tips are optional and go one hundred percent to your creator. Creators receive payout summaries by email after every order.",
  },
  {
    q: "How do I delete my account?",
    a: "In the app, open Profile and tap Delete Account. This permanently removes your account and personal data. You can also visit our Delete Account page for details.",
    link: { to: "/delete-account", label: "Go to Delete Account" },
  },
  {
    q: "Is Dishly safe?",
    a: "Creators are reviewed and can display food-safety verification badges. A creator's exact address stays private — customers only see distance until an order is placed. Every rating comes from a verified, completed order, so reviews are always trustworthy. Dishly is for users 18 and older.",
  },
];

export default function Support() {
  return (
    <PageShell title="Support" testId="support-page">
      <div className="mx-auto max-w-4xl px-5 lg:px-8">
        <p className="text-[12px] font-bold uppercase tracking-widest text-terracotta">Help center</p>
        <h1 className="mt-3 text-4xl font-black leading-tight tracking-tight text-charcoal sm:text-5xl">
          How can we help?
        </h1>
        <p className="mt-4 max-w-2xl text-base text-charcoal/65 sm:text-lg">
          Answers to the most common questions, plus a direct line to our team.
        </p>

        {/* Contact block */}
        <div className="mt-10 rounded-2xl border border-black/5 bg-white p-6 sm:p-8" data-testid="support-contact-block">
          <p className="text-[12px] font-bold uppercase tracking-widest text-charcoal/50">Contact us</p>
          <div className="mt-3 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-terracotta/10 text-terracotta">
                <Mail className="h-5 w-5" />
              </span>
              <div>
                <a
                  href="mailto:support@dinewithdishly.co"
                  data-testid="support-email-link"
                  className="text-lg font-bold text-charcoal hover:text-terracotta"
                >
                  support@dinewithdishly.co
                </a>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-charcoal/55">
                  <Clock className="h-3.5 w-3.5" /> We reply within 1–2 business days.
                </p>
              </div>
            </div>
            <a
              href="mailto:support@dinewithdishly.co"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-5 py-3 text-[14px] font-bold text-white transition-transform hover:scale-[1.02]"
              data-testid="support-email-btn"
            >
              Email support <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>

        {/* FAQ */}
        <h2 className="mt-14 text-2xl font-black text-charcoal sm:text-3xl">Frequently asked questions</h2>
        <div className="mt-6 rounded-2xl border border-black/5 bg-white" data-testid="support-faq">
          <Accordion type="single" collapsible className="divide-y divide-black/5">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-none px-6" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="py-5 text-left text-[16px] font-bold text-charcoal hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-[15px] leading-relaxed text-charcoal/70">
                  <p>{f.a}</p>
                  {f.link && (
                    <Link
                      to={f.link.to}
                      className="mt-3 inline-flex items-center gap-1 text-terracotta hover:underline"
                      data-testid={`faq-link-${i}`}
                    >
                      {f.link.label} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <p className="mt-12 text-[14px] text-charcoal/55">
          Still stuck? Email us at{" "}
          <a href="mailto:support@dinewithdishly.co" className="font-semibold text-terracotta hover:underline">
            support@dinewithdishly.co
          </a>{" "}
          and we'll get back to you within 1–2 business days.
        </p>
      </div>
    </PageShell>
  );
}
